/**
 * React Query hooks for the service catalog.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import {
  getServiceProducts,
  createServiceProduct,
  updateServiceProduct,
  deleteServiceProduct,
  getServiceStats,
} from '../services/serviceProductService';
import type { ServiceProductFilters, ServiceProductFormData } from '../types';

const KEY = 'service-products';

export function useServiceProducts(filters?: ServiceProductFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [KEY, 'list', filters ?? {}],
    queryFn: () => getServiceProducts(filters),
    enabled: options?.enabled ?? true,
  });
}

/** Operational KPIs for the Services page strips (catalog + runs). */
export function useServiceStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [KEY, 'stats'],
    queryFn: getServiceStats,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateServiceProduct() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ data, locationId }: { data: ServiceProductFormData; locationId: string }) =>
      createServiceProduct(data, locationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      notify.success(t('services.catalog.created', 'Service created'));
    },
    onError: () => notify.error(t('services.catalog.saveError', 'Could not save the service')),
  });
}

export function useUpdateServiceProduct() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ServiceProductFormData> }) =>
      updateServiceProduct(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      notify.success(t('services.catalog.updated', 'Service updated'));
    },
    onError: () => notify.error(t('services.catalog.saveError', 'Could not save the service')),
  });
}

export function useDeleteServiceProduct() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => deleteServiceProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      notify.success(t('services.catalog.deleted', 'Service deleted'));
    },
    onError: () => notify.error(t('services.catalog.deleteError', 'Could not delete the service')),
  });
}
